import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Briefcase,
  Hash,
  MessageSquare,
  AlertCircle,
  Calendar,
  Users,
  Save
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import styles from './TaskDrawer.module.css';

const TaskDrawer = ({ isOpen, onClose, taskData = null, onSave }) => {
  const [formData, setFormData] = useState({
    project_id: null,
    title: '',
    description: '',
    priority: { value: 'medium', label: 'Medium' },
    status: { value: 'open', label: 'Open' },
    due_date: '',
    employee_ids: []
  });

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);

  const mode = taskData ? 'edit' : 'add';

  const fetchDependencies = useCallback(async () => {
    setIsFetching(true);
    try {
      // Step 1: Fetch master data (projects and employees)
      const [projectsResult, employeesResult] = await Promise.allSettled([
        api.get('/projects-dropdown'),
        api.get('/employees-dropdown')
      ]);

      let projectsData = [];
      let employeesData = [];

      if (projectsResult.status === 'fulfilled' && projectsResult.value.data.success) {
        projectsData = projectsResult.value.data.data.map(p => ({ value: p.id, label: p.project_name }));
      } else if (projectsResult.status === 'rejected') {
        const code = projectsResult.reason?.response?.status;
        if (code !== 404 && code !== 403) {
          console.error('Error fetching projects dropdown:', projectsResult.reason);
        }
      }
      setProjects(projectsData);

      if (employeesResult.status === 'fulfilled' && employeesResult.value.data.success) {
        employeesData = employeesResult.value.data.data.map(e => ({ value: e.id, label: e.employee_name }));
      } else if (employeesResult.status === 'rejected') {
        const code = employeesResult.reason?.response?.status;
        if (code !== 404 && code !== 403) {
          console.error('Error fetching employees dropdown:', employeesResult.reason);
        }
      }
      setEmployees(employeesData);

      // Step 2: If editing, fetch FRESH details for this specific task
      if (mode === 'edit' && taskData?.id) {
        const taskRes = await api.get(`/tasks/${taskData.id}`, { skipLoader: true });

        if (taskRes.data.success) {
          const freshTask = taskRes.data.data;

          setFormData({
            project_id: projectsData.find(p => p.value === freshTask.project_id) ||
              (freshTask.project ? { value: freshTask.project.id, label: freshTask.project.project_name } : null),
            title: freshTask.title || '',
            description: freshTask.description || '',
            priority: {
              value: freshTask.priority,
              label: (freshTask.priority?.charAt(0).toUpperCase() + freshTask.priority?.slice(1)) || 'Medium'
            },
            status: {
              value: freshTask.status,
              label: freshTask.status?.replace('_', ' ').toUpperCase() || 'OPEN'
            },
            due_date: freshTask.due_date || '',
            employee_ids: freshTask.assigned_employees?.map(e => ({
              value: e.id,
              label: e.employee_name
            })) || []
          });
        }
      } else {
        // Reset for Add mode
        setFormData({
          project_id: null,
          title: '',
          description: '',
          priority: { value: 'medium', label: 'Medium' },
          status: { value: 'open', label: 'Open' },
          due_date: '',
          employee_ids: []
        });
      }
    } catch (error) {
      console.error('Error fetching task dependencies:', error);
      toast.error('Failed to load form data');
    } finally {
      setIsFetching(false);
    }
  }, [mode, taskData]);

  useEffect(() => {
    if (isOpen) {
      fetchDependencies();
    }
  }, [isOpen, fetchDependencies]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.project_id) {
      toast.error('Please fill in required fields (Title and Project)');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        project_id: formData.project_id.value,
        priority: formData.priority.value,
        status: formData.status.value,
        employee_ids: formData.employee_ids.map(e => e.value)
      };

      let response;
      if (mode === 'edit') {
        response = await api.put(`/tasks/${taskData.id}`, payload);
      } else {
        response = await api.post('/tasks', payload);
      }

      if (response.data.success) {
        toast.success(`Task ${mode === 'edit' ? 'updated' : 'created'} successfully`);
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(error.response?.data?.message || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles['drawer-overlay']} ${isOpen ? styles['open'] : ''}`} onClick={onClose}>
      <div className={`${styles['drawer-content']} ${isOpen ? styles['open'] : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{mode === 'add' ? 'Create New Task' : 'Edit Task'}</h2>
            <p>{mode === 'add' ? 'Assign a new work item to the project' : 'Update task details and progress'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSave} className={styles['drawer-form']}>
          {isFetching ? (
            <div className={styles['fetching-state']}>
              <div className={styles['spinner']}></div>
              <p>Fetching technical details...</p>
            </div>
          ) : (
            <>
              <div className={styles['form-scroll-area']}>
                <div className={styles['form-group']}>
                  <label><Briefcase size={16} /> Associated Project <span className={styles['required-star']}>*</span></label>
                  <CustomSelect
                    options={projects}
                    value={formData.project_id}
                    onChange={(val) => setFormData(prev => ({ ...prev, project_id: val }))}
                    placeholder="Search and select project..."
                    isSearchable={true}
                    isClearable={true}
                    isDisabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><Hash size={16} /> Task Title <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    name="title"
                    className={styles['input-field']}
                    placeholder="e.g. Finalize structural diagrams"
                    value={formData.title}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><MessageSquare size={16} /> Description</label>
                  <textarea
                    name="description"
                    className={styles['textarea-field']}
                    placeholder="Enter technical specifications or notes..."
                    rows="4"
                    value={formData.description}
                    onChange={handleInputChange}
                    disabled={loading}
                  ></textarea>
                </div>

                <div className={styles['form-row']}>
                  <div className={styles['form-group']}>
                    <label><AlertCircle size={16} /> Status</label>
                    <CustomSelect
                      options={[
                        { value: 'open', label: 'Open' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'overdue', label: 'Overdue' }
                      ]}
                      value={formData.status}
                      onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                      isDisabled={loading}
                    />
                  </div>

                  <div className={styles['form-group']}>
                    <label><Calendar size={16} /> Priority</label>
                    <CustomSelect
                      options={[
                        { value: 'high', label: 'High' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'low', label: 'Low' }
                      ]}
                      value={formData.priority}
                      onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                      isDisabled={loading}
                    />
                  </div>
                </div>

                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Due Date</label>
                  <CustomDatePicker
                    selected={formData.due_date}
                    onChange={(date) => setFormData(prev => ({ ...prev, due_date: date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '' }))}
                    placeholderText="Select deadline"
                    disabled={loading}
                  />
                </div>

                <div className={styles['form-group']}>
                  <label><Users size={16} /> Shared With Employees</label>
                  <CustomSelect
                    isMulti
                    options={employees}
                    value={formData.employee_ids}
                    onChange={(val) => setFormData(prev => ({ ...prev, employee_ids: val || [] }))}
                    placeholder="Select team members..."
                    isSearchable={true}
                    isDisabled={loading}
                  />
                </div>
              </div>

              <div className={styles['drawer-footer']}>
                <button type="button" className={styles['cancel-btn']} onClick={onClose} disabled={loading}>
                  Cancel
                </button>
                <div className={styles['footer-actions']}>
                  <button type="submit" className={styles['save-btn']} disabled={loading}>
                    {loading ? <div className={styles['spinner']} /> : <Save size={18} />}
                    <span>{mode === 'add' ? 'Save Task' : 'Update Task'}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default TaskDrawer;
